import React from 'react';
import { defineMessages, t } from '@sm/intl';
import { createURL } from '@sm/utils';

import EndpageHeader from '~app/components/EndpageHeader';
import RebrandEpFormCore from '~app/components/RebrandEpFormCore';
import { ProductsCalloutListItem } from '~app/components/ProductsCalloutList/ProductsCalloutList';

import PerksIconWhite from '~static/images/icons/perks-icon-white.svg';
import MsgIconWhite from '~static/images/icons/msg-icon-white.svg';
import SparkleWhite from '~static/images/brazilExp/icons/sparkle-white.svg';
import { getSrc } from '~app/helpers/assetLoading';
import { BrazilEpProps } from '~helpers/types';

const COPY = defineMessages({
  ENDPAGE_HEADER_COPY: {
    id: 'SurveyThanks.RebrandEpFormIntl.EndpageHeaderCopy',
    defaultMessage: 'Thank you for taking this survey.',
    description: '[Type: Header][Vis: med] - Informs the viewer that this survey has just completed',
  },

  PRODUCT_CALLOUT_PERKS_HEADING: {
    id: 'SurveyThanks.RebrandEpFormIntl.ProductCalloutPerksHeader',
    defaultMessage: 'Is your company missing key perks?',
    description:
      '[Type: Header][Vis: med] - Tagline to help entice users to views click the cta below. The CTA leads to another signup page with minimal information.',
  },
  PRODUCT_CALLOUT_PERKS_BODY: {
    id: 'SurveyThanks.RebrandEpFormIntl.ProductCalloutPerkseBody',
    defaultMessage:
      'People love their work perks. Send a survey to find out if you’re offering the incentives employees and potential candidates want.',
    description: '[Type: Paragraph][Vis: med] - Gives context to the above Tagline',
  },
  PRODUCT_CALLOUT_PERKS_CTA: {
    id: 'SurveyThanks.RebrandEpFormIntl.ProductCalloutPerksCTA',
    defaultMessage: 'Find out',
    description: '[Type: Button][Vis: med] - The text for a button that leads users to another sign up page',
  },
  PRODUCT_CALLOUT_COLLABORATION_HEADING: {
    id: 'SurveyThanks.RebrandEpFormIntl.ProductCalloutCollaborationEUHeader',
    defaultMessage: 'Are your customers satisfied?',
    description: '[Type: Header][Vis: med] - Tag line to help user learn more about SurveyMonkey Collaboration',
  },
  PRODUCT_CALLOUT_COLLABORATION_BODY: {
    id: 'SurveyThanks.RebrandEpFormIntl.ProductCalloutCollaborationEUBody',
    defaultMessage: '83% of businesses who describe themselves as “successful” actively measure customer satisfaction.',
    description: '[Type: Paragraph][Vis: med] - Expands and reiterate the above header line.',
  },
  PRODUCT_CALLOUT_COLLABORATION_CTA: {
    id: 'SurveyThanks.RebrandEpFormIntl.ProductCalloutCollaborationEUCTA',
    defaultMessage: 'Get started',
    description:
      "[Type: Button][Vis: med] - The button text which leads users to a marketing page to learn more about SurveyMonkey' premium plans",
  },
});

const BRAZIL_ENDPAGE_COPY = {
  BRAZIL_FORM_CTA_HEADER: 'Obtenha respostas para suas perguntas',
  BRAZIL_FORM_CTA_SUBHEADER:
    'Junte-se a milhões de usuários que confiam na SurveyMonkey para criar questionários, compartilhá-los pelo WhatsApp e analisá-los, tudo otimizado para aparelhos móveis.',
  BRAZIL_PRODUCT_CALLOUT_HEADER: 'Crie pesquisas poderosas facilmente',
  BRAZIL_PRODUCT_CALLOUT_CTA: 'Explore a plataforma',
  BRAZIL_PRODUCT_CALLOUT_BODY:
    'Crie uma pesquisa em cerca de 60 segundos com IA. De enquetes rápidas a pesquisas globais, nossa plataforma intuitiva responde a mais de 20 milhões de perguntas todos os dias.',
  BRAZIL_BRANDS_CALLOUT_HEADER: 'As marcas de confiança do Brasil escolhem o SurveyMonkey',
  BRAZIL_BRANDS_SIGNUP_CTA: 'Cadastre-se gratuitamente',
};

function RebrandEpFormIntl({ treatment }: { treatment?: string }): React.ReactElement {
  const utSource = 'survey-thanks';
  const utSource2 = treatment === 'treatment1' ? 'EpFormBrazil' : 'RebrandEpFormIntl';
  const utSource3 = treatment === 'treatment1' ? 'BrazilEndpage' : 'ProductCalloutItem';

  const CALLOUT_ITEM_SIGN_UP: ProductsCalloutListItem = {
    icon: getSrc(PerksIconWhite),
    heading: t(COPY.PRODUCT_CALLOUT_PERKS_HEADING),
    copy: t(COPY.PRODUCT_CALLOUT_PERKS_BODY),
    href: createURL('/user/sign-up/', {
      ut_source: utSource,
      ut_source2: utSource2,
      ut_source3: utSource3,
      ut_ctatext: COPY.PRODUCT_CALLOUT_PERKS_CTA.defaultMessage,
    }),
    ctaText: COPY.PRODUCT_CALLOUT_PERKS_CTA,
    ctaUsage: 'sign-up secondary',
    ctaColor: 'sabaeus',
    name: 'perks',
  };

  const CALLOUT_ITEM_COLLABORATION: ProductsCalloutListItem = {
    icon: getSrc(MsgIconWhite),
    heading: t(COPY.PRODUCT_CALLOUT_COLLABORATION_HEADING),
    copy: t(COPY.PRODUCT_CALLOUT_COLLABORATION_BODY),
    href: createURL('/mp/collaboration/', {
      ut_source: utSource,
      ut_source2: utSource2,
      ut_source3: utSource3,
      ut_ctatext: COPY.PRODUCT_CALLOUT_COLLABORATION_CTA.defaultMessage,
    }),
    ctaText: COPY.PRODUCT_CALLOUT_COLLABORATION_CTA,
    ctaUsage: 'collaboration',
    ctaColor: 'concord',
    name: 'collaboration',
  };

  const BRAZIL_CALLOUT_ITEM: BrazilEpProps = {
    icon: getSrc(SparkleWhite),
    formHeader: BRAZIL_ENDPAGE_COPY.BRAZIL_FORM_CTA_HEADER,
    formSubHeader: BRAZIL_ENDPAGE_COPY.BRAZIL_FORM_CTA_SUBHEADER,
    heading: BRAZIL_ENDPAGE_COPY.BRAZIL_PRODUCT_CALLOUT_HEADER,
    copy: BRAZIL_ENDPAGE_COPY.BRAZIL_PRODUCT_CALLOUT_BODY,
    href: createURL('https://pt.surveymonkey.com/product/', {
      ut_source: utSource,
      ut_source2: utSource2,
      ut_source3: utSource3,
      ut_ctatext: BRAZIL_ENDPAGE_COPY.BRAZIL_PRODUCT_CALLOUT_CTA,
    }),
    signupHref: createURL('/user/sign-up/', {
      ut_source: utSource,
      ut_source2: utSource2,
      ut_source3: utSource3,
      ut_ctatext: BRAZIL_ENDPAGE_COPY.BRAZIL_BRANDS_SIGNUP_CTA,
    }),
    brandHeading: BRAZIL_ENDPAGE_COPY.BRAZIL_BRANDS_CALLOUT_HEADER,
    ctaText: BRAZIL_ENDPAGE_COPY.BRAZIL_PRODUCT_CALLOUT_CTA,
    ctaSignupText: BRAZIL_ENDPAGE_COPY.BRAZIL_BRANDS_SIGNUP_CTA,
    ctaUsage: 'explore the product',
    name: 'brazil product callout',
  };

  return (
    <>
      <EndpageHeader copy={t(COPY.ENDPAGE_HEADER_COPY)} hasLogo headerColor="sabaeus" headerType="thanks" />
      <RebrandEpFormCore
        utSource={utSource}
        utSource2={utSource2}
        calloutItem1={CALLOUT_ITEM_SIGN_UP}
        calloutItem2={CALLOUT_ITEM_COLLABORATION}
        calloutBrazil={BRAZIL_CALLOUT_ITEM}
        treatment={treatment}
      />
    </>
  );
}

export default RebrandEpFormIntl;
